import apiClient from './config/axios';

/**
 * Notification API module
 * Handles all notification-related API calls
 */
export const notificationApi = {
    // ===== USER NOTIFICATIONS =====

    /**
     * Get all notifications for the current user
     * @param {object} params - Query parameters
     * @param {string} [params.type] - Filter by type (ORDER, PROMOTION, SYSTEM)
     * @param {boolean} [params.unreadOnly] - Only return unread notifications
     * @param {number} [params.page] - Page number
     * @param {number} [params.limit] - Items per page
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getNotifications: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.type) queryParams.append('type', params.type);
            if (params.unreadOnly) queryParams.append('unreadOnly', 'true');
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);

            const queryString = queryParams.toString();
            const url = queryString ? `/api/notifications?${queryString}` : '/api/notifications';
            
            const response = await apiClient.get(url);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch notifications',
            };
        }
    },

    /**
     * Get unread notification count
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getUnreadCount: async () => {
        try {
            const response = await apiClient.get('/api/notifications/unread-count');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch unread count',
            };
        }
    },

    /**
     * Mark a single notification as read
     * @param {number} notificationId - Notification ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    markAsRead: async (notificationId) => {
        try {
            const response = await apiClient.put(`/api/notifications/${notificationId}/read`);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to mark notification as read',
            };
        }
    },

    /**
     * Mark all notifications as read
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    markAllAsRead: async () => {
        try {
            const response = await apiClient.put('/api/notifications/read-all');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to mark all notifications as read',
            };
        }
    },

    /**
     * Delete a single notification
     * @param {number} notificationId - Notification ID
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    deleteNotification: async (notificationId) => {
        try {
            const response = await apiClient.delete(`/api/notifications/${notificationId}`);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete notification',
            };
        }
    },

    /**
     * Delete all notifications
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    deleteAllNotifications: async () => {
        try {
            const response = await apiClient.delete('/api/notifications/all');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete all notifications',
            };
        }
    },

    // ===== NOTIFICATION PREFERENCES =====

    /**
     * Get notification preferences
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getPreferences: async () => {
        try {
            const response = await apiClient.get('/api/notifications/preferences');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch notification preferences',
            };
        }
    },

    /**
     * Update notification preferences
     * @param {object} preferences - Preference settings
     * @param {boolean} [preferences.orderUpdates] - Order update notifications
     * @param {boolean} [preferences.promotions] - Promotional notifications
     * @param {boolean} [preferences.systemAlerts] - System alert notifications
     * @param {boolean} [preferences.emailEnabled] - Email delivery
     * @param {boolean} [preferences.pushEnabled] - Push delivery
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    updatePreferences: async (preferences) => {
        try {
            const response = await apiClient.put('/api/notifications/preferences', preferences);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update notification preferences',
            };
        }
    },
};

export default notificationApi;
